import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarDays, FlaskConical, Stethoscope } from 'lucide-react';
import { Button } from '../components/Button';
import { StatusMarker } from '../components/StatusMarker';
import { usePlatform } from '../contexts/PlatformContext';
import { labReport } from '../data/biomarkers';
import { getStatus } from '../utils/biomarkers';
export function Profile() {
  const {
    user,
    goal,
    stage,
    panel
  } = usePlatform();
  const navigate = useNavigate();
  if (!user || !goal) return <Navigate to="/" replace data-id="element-210" data-mp="4tbjzec7q9rm" />;
  const flagged = panel.filter(b => getStatus(b) !== 'optimal').length;
  const initials = user.name.split(' ').map(n => n[0]).join('').slice(0, 2);
  const details = [{
    label: 'Age',
    value: `${user.age}`
  }, {
    label: 'Location',
    value: user.location
  }, {
    label: 'Height',
    value: `${user.heightCm} cm`
  }, {
    label: 'Weight',
    value: `${user.weightKg} kg`
  }, {
    label: 'Blood type',
    value: user.bloodType
  }, {
    label: 'Member since',
    value: user.memberSince
  }];
  return <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]" data-id="element-211" data-mp="264cme7c23b0w">
      <section aria-labelledby="profile-heading" className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8" data-id="element-212" data-mp="265w504l6py5u">
        <div className="flex flex-wrap items-center gap-5" data-id="element-213" data-mp="2k1ht89jlgdx7">
          <span className="flex size-16 items-center justify-center rounded-full border border-[#9b8cf0]/50 bg-[#9b8cf0]/10 text-xl font-medium text-[#c4baff]" data-id="element-214" data-mp="2f36g9py05nar">
            {initials}
          </span>
          <div className="flex-1" data-id="element-215" data-mp="2p2fif630u887">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-216" data-mp="32uc7f19e820d">Your profile</p>
            <h1 id="profile-heading" className="mt-1 text-2xl font-medium tracking-tight md:text-3xl" data-id="element-217" data-mp="2xvisd4egk2y3">{user.name}</h1>
            <p className="text-sm text-muted-foreground" data-id="element-218" data-mp="3crzzfjfm6veu">{user.email}</p>
          </div>
          <StatusMarker goal={goal} stage={stage} data-id="element-219" data-mp="37topz9t8emx5" />
        </div>

        <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-3" data-id="element-220" data-mp="37rc3cjcj3odr">
          {details.map(d => <div key={d.label} className="bg-[#0f1017] p-4" data-id="element-221" data-mp="3jwhoqgvimzij">
              <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-222" data-mp="3ov2pqiildn81">{d.label}</dt>
              <dd className="mt-1 font-medium" data-id="element-223" data-mp="3tlsvhwuxjs39">{d.value}</dd>
            </div>)}
        </dl>

        <div className="mt-8 rounded-2xl border border-white/10 p-5" data-id="element-224" data-mp="2fzrgzkep9kw">
          <div className="flex items-center justify-between gap-4" data-id="element-225" data-mp="6gtg5v5oaseu">
            <div data-id="element-226" data-mp="6f0bglemei9h">
              <p className="font-medium" data-id="element-227" data-mp="6io4zkk2f4o7">Health status</p>
              <p className="text-sm text-muted-foreground" data-id="element-228" data-mp="bh7qjjuqyeg1">
                {goal === 'pregnancy' ? 'Your results and packs follow your pregnancy stage.' : 'Your results and packs focus on long-term wellbeing.'}
              </p>
            </div>
            <StatusMarker goal={goal} stage={stage} size="sm" data-id="element-229" data-mp="2i7fb51zdxurz" />
          </div>
        </div>
      </section>

      <div className="space-y-6" data-id="element-230" data-mp="2anwis5494p6q">
        <section className="rounded-3xl border border-[#9b8cf0]/40 bg-[#9b8cf0]/[0.05] p-6 md:p-8" data-id="element-231" data-mp="2aoxzap41e3vu">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#c4baff]" data-id="element-232" data-mp="25u1ueodpbm1b">
            <FlaskConical className="size-3.5" data-id="element-233" data-mp="20upaoci4mv2y" /> Latest blood test
          </p>
          <p className="mt-4 text-4xl font-medium tracking-tight" data-id="element-234" data-mp="1vzrxquhq81ks">{panel.length}</p>
          <p className="text-muted-foreground" data-id="element-235" data-mp="2tl79v2fqccb3">biomarkers analysed</p>
          <div className="mt-5 flex gap-6 text-sm" data-id="element-236" data-mp="2omct061boxtm">
            <p data-id="element-237" data-mp="2okjmqrumgfm5"><span className="font-medium text-[#8fd3b0]" data-id="element-238" data-mp="2okrxoe76paup">{panel.length - flagged}</span> in range</p>
            <p data-id="element-239" data-mp="2jpcmxv4h3v2i"><span className="font-medium text-[#f5b454]" data-id="element-240" data-mp="2jpcmju0k9t1z">{flagged}</span> need attention</p>
          </div>
          <p className="mt-5 flex items-center gap-2 text-sm text-muted-foreground" data-id="element-241" data-mp="188cvt2tv3ma2">
            <CalendarDays className="size-4" data-id="element-242" data-mp="18603pwue3vja" /> {labReport.lab} · {labReport.date}
          </p>
          <Button size="lg" className="mt-6 w-full bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" onClick={() => navigate('/results')} data-id="element-243" data-mp="13bdyycitd186">
            View my results <ArrowRight className="size-4" data-id="element-244" data-mp="2ww0du17ag6iw" />
          </Button>
        </section>

        <section className="flex items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.02] p-6" data-id="element-245" data-mp="2i749ae4fxbn1">
          <span className="flex size-11 items-center justify-center rounded-full border border-white/15" data-id="element-246" data-mp="2n29sv6adee7y">
            <Stethoscope className="size-5 text-[#c4baff]" data-id="element-247" data-mp="3as43rib4pspk" />
          </span>
          <div data-id="element-248" data-mp="3fsjiad75d694">
            <p className="font-medium" data-id="element-249" data-mp="3fqyud6fc8lk8">{labReport.reviewer}</p>
            <p className="text-sm text-muted-foreground" data-id="element-250" data-mp="30uzevapwfj52">Your family-medicine doctor · verifies every pack</p>
          </div>
        </section>
      </div>
    </div>;
}